import { TextField } from '@mui/material';
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { EngagementPlan } from '@redux/tasks/tasksTypes';
import EngagementPlanDefaultImage2 from 'assets/images/EngagementPlanDefaultImage2.png';
import { Button } from 'components/Button/Button';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { makeCustomInputStyles } from '../../../../customStyles';
import style from './UpdateImageArea.scss';

type Props = {
  imageUrl?: string;
  setImageUrl: Dispatch<SetStateAction<EngagementPlan>>;
};

const doesImageExist = (url: string) =>
  new Promise((resolve) => {
    const img = new Image();

    img.src = url;
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });

export const UpdateImageArea: React.FC<Props> = ({ imageUrl, setImageUrl }) => {
  const customInputStyles = makeCustomInputStyles();

  const [editMode, setEditMode] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [error, setError] = useState<string>('');
  const [checkErrorLoading, setCheckErrorLoading] = useState<boolean>(false);

  const validateImageUrl = useCallback(
    async (url: string) => {
      if (!url?.length) {
        setError('');
        setCheckErrorLoading(false);
        return;
      }

      try {
        setCheckErrorLoading(true);
        const exists = await doesImageExist(url);
        if (!exists) {
          setError('Image not found. Please check the URL and try again.');
        } else {
          setError('');
        }
      } catch {
        setError('Error validating image URL. Please try again.');
      } finally {
        setCheckErrorLoading(false);
      }
    },
    [setError, setCheckErrorLoading]
  );

  const clearInput = useCallback(() => {
    setEditMode(false);
    setNewImageUrl('');
    setError('');
    setCheckErrorLoading(false);
  }, [setError, setNewImageUrl, setCheckErrorLoading]);

  const imageLink = error ? EngagementPlanDefaultImage2 : (!checkErrorLoading && newImageUrl) || imageUrl || EngagementPlanDefaultImage2;

  return (
    <div className={style.container}>
      <div className={style.contentWrapper}>
        <div className={style.imagePreview} style={{ backgroundImage: `url(${imageLink})` }} />
        <div className={style.textWrapper}>
          <Text variant="md">Cover image</Text>
          <Text variant="sm" color={Color.GRAY}>
            We recommend image size 860x640px
          </Text>
        </div>
        {!editMode && (
          <Button variant="outlined" onClick={() => setEditMode(true)}>
            Update image
          </Button>
        )}
      </div>
      {editMode && (
        <div className={style.editWrapper}>
          <Text variant="sm" color={Color.GRAY}>
            Insert image url
          </Text>
          <TextField
            name="imageUrl"
            variant="outlined"
            value={newImageUrl}
            onChange={(value) => {
              validateImageUrl(value.target.value);
              setNewImageUrl(value.target.value);
            }}
            className={customInputStyles.input}
            autoComplete="off"
          />
          {error ? (
            <Text variant="sm" className={style.error}>
              {error}
            </Text>
          ) : null}
          <div className={style.buttonsWrapper}>
            <Button
              variant="outlined"
              onClick={() => {
                setImageUrl((prevState) => ({
                  ...prevState,
                  imageSource: newImageUrl
                }));
                clearInput();
              }}
              disabled={!newImageUrl.length || !!error}
            >
              Update
            </Button>
            <Button variant="text" onClick={clearInput} className={style.textButton}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
